import React, { useMemo, useState } from 'react';
import { Search, Trash2, ChevronDown, ChevronUp, Receipt, BadgePercent, Package as PackageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { deleteSale } from '../../lib/salesStorage';
import { money } from '../../lib/services';

export default function SalesTab({ sales, onChange }) {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sales;
    return sales.filter((s) =>
      [s.clientName, s.id, ...s.items.map((i) => i.serviceName)].some((f) =>
        String(f || '').toLowerCase().includes(q)
      )
    );
  }, [sales, query]);

  const handleDelete = (id) => {
    deleteSale(id);
    setConfirmDelete(null);
    onChange?.();
    toast.success('Sale deleted.');
  };

  const stats = useMemo(() => {
    const total = sales.reduce((s, x) => s + x.total, 0);
    return { count: sales.length, total };
  }, [sales]);

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="relative w-full lg:max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search sales by client or service..."
            className="w-full h-14 pl-12 pr-4 rounded-2xl border border-white/10 bg-white text-slate-100 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 focus:outline-none"
            data-testid="sales-search-input"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 h-11 px-3 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-semibold">
            <Receipt className="w-4 h-4" />
            {stats.count} sales
          </div>
          <div className="flex items-center gap-2 h-11 px-3 rounded-full bg-white border border-white/10 text-slate-200 text-sm font-semibold">
            Revenue: <span className="text-blue-300">{money(stats.total)}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-white/10 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="px-6 py-16 text-center text-slate-400">
            {sales.length === 0
              ? 'No sales yet. Head to “New Sale” to process a transaction.'
              : 'No sales match your search.'}
          </div>
        ) : (
          <div data-testid="sales-list">
            {filtered.map((s) => {
              const isOpen = expanded === s.id;
              return (
                <div
                  key={s.id}
                  className="border-b border-white/10 last:border-b-0"
                  data-testid={`sales-row-${s.id}`}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : s.id)}
                    className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-blue-500/10/30 transition"
                    data-testid={`sales-row-toggle-${s.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-white">
                          {s.clientName}
                        </p>
                        {s.isWalkIn && (
                          <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-400 bg-slate-900 rounded-full px-2 py-0.5">
                            Walk-in
                          </span>
                        )}
                        {s.bundleSavings > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-bold text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5">
                            <BadgePercent className="w-3 h-3" />
                            Bundle
                          </span>
                        )}
                        {s.items.some((i) => i.sessions > 1) && (
                          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.1em] uppercase font-bold text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full px-2 py-0.5">
                            <PackageIcon className="w-3 h-3" />
                            Package
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(s.createdAt).toLocaleString()} · {s.items.length} item{s.items.length > 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <p className="text-lg font-display text-white" data-testid={`sales-row-total-${s.id}`}>
                        {money(s.total)}
                      </p>
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 pt-1 bg-slate-950/50 border-t border-white/10" data-testid={`sales-row-detail-${s.id}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Items</p>
                          <ul className="space-y-2">
                            {s.items.map((it, idx) => (
                              <li key={idx} className="p-3 rounded-xl bg-white border border-white/10">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold text-white">{it.serviceName}</p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                      {it.sessions} × {money(it.unitPrice)}
                                      {it.bundled && (
                                        <span className="ml-2 text-blue-400 font-semibold">bundled</span>
                                      )}
                                      {it.tier.discountPct > 0 && (
                                        <span className="ml-2 text-blue-400 font-semibold">−{it.tier.discountPct}%</span>
                                      )}
                                    </p>
                                  </div>
                                  <p className="text-sm font-semibold text-white shrink-0">
                                    {money(it.lineTotal)}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">Totals</p>
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span>{money(s.subtotal)}</span></div>
                            {s.bundleSavings > 0 && (
                              <div className="flex justify-between text-blue-400"><span>Bundle savings</span><span>− {money(s.bundleSavings)}</span></div>
                            )}
                            {s.packageDiscount > 0 && (
                              <div className="flex justify-between text-blue-400"><span>Package discount</span><span>− {money(s.packageDiscount)}</span></div>
                            )}
                            <div className="flex justify-between pt-2 mt-2 border-t border-dashed border-white/10 font-semibold text-white">
                              <span>Total</span><span>{money(s.total)}</span>
                            </div>
                          </div>
                          <div className="mt-6">
                            <button
                              onClick={() => setConfirmDelete(s)}
                              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-white/10 hover:border-red-300 hover:text-red-600 text-slate-300 text-sm font-semibold"
                              data-testid={`sales-delete-${s.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete sale
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="sales-delete-modal"
        >
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6">
              <h3 className="font-display text-2xl text-slate-100">Delete sale?</h3>
              <p className="mt-2 text-slate-300">
                This will remove the sale for <span className="font-semibold text-slate-100">{confirmDelete.clientName}</span> totalling <span className="font-semibold text-slate-100">{money(confirmDelete.total)}</span>. Associated packages will remain unless deleted separately.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setConfirmDelete(null)} className="h-11 px-5 rounded-full bg-slate-900 hover:bg-slate-800 text-slate-200 font-semibold text-sm">Cancel</button>
                <button onClick={() => handleDelete(confirmDelete.id)} className="h-11 px-5 rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold text-sm" data-testid="sales-delete-confirm">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
